import PropTypes from "prop-types";
import { createContext, useEffect } from "react";
import { useImmerReducer } from "use-immer";

import axiosInstance from "src/utils/axios";
import { setProfileIdSession, setSession } from "src/utils/jwt";

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  profileId: null,
  profiles: [],
  user: {},
};

const handlers = {
  INITIALIZE: (state, action) => {
    state.isAuthenticated = action.payload.isAuthenticated;
    state.isInitialized = true;
    state.user = action.payload.user;
  },
  LOGIN: (state, action) => {
    state.isAuthenticated = true;
    state.user = action.payload.user;
  },
  LOGOUT: (state) => {
    state.isAuthenticated = false;
    state.profileId = null;
    state.user = {};
  },
  REGISTER: (state, action) => {
    state.isAuthenticated = true;
    state.user = action.payload.user;
  },
  SET_PROFILES: (state, action) => {
    state.profiles = action.payload;
  },
  SWITCH_PROFILE: (state, action) => {
    state.profileId = action.payload;
  },
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext({
  ...initialState,
  login: () => Promise.resolve(),
  getProfiles: () => Promise.resolve(),
  switchProfile: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  getCode: () => Promise.resolve(),
  verifyEmail: () => Promise.resolve(),
});

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useImmerReducer(reducer, initialState);

  useEffect(() => {
    (async function () {
      try {
        const accessToken = window.localStorage.getItem("accessToken");
        const refreshToken = window.localStorage.getItem("refreshToken");
        const profileId = window.localStorage.getItem("wp");
        if (accessToken) {
          setSession(accessToken, refreshToken);
          const response = await axiosInstance.get("/members/me");
          const { user } = response.data;
          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: true,
              user,
            },
          });
          if (profileId) {
            switchProfile(profileId);
          }
        } else {
          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: false,
              user: {},
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: false,
            user: {},
          },
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state.isAuthenticated) {
      getProfiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isAuthenticated]);

  const login = async (values) => {
    const response = await axiosInstance.post("/members/login", values);
    // eslint-disable-next-line camelcase
    const { access_token, refresh_token, user } = response.data;
    setSession(access_token, refresh_token);
    dispatch({
      type: "LOGIN",
      payload: {
        user,
      },
    });
  };

  const getProfiles = async () => {
    await axiosInstance
      .get("/watchprofile")
      .then((response) => {
        dispatch({ type: "SET_PROFILES", payload: response.data });
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const switchProfile = (pid) => {
    if (state.profileId !== pid) {
      setProfileIdSession(pid);
      dispatch({ type: "SWITCH_PROFILE", payload: pid });
    }
  };

  const register = async (values) => {
    const response = await axiosInstance.post("/members", values);
    return response;
  };

  const logout = async () => {
    await axiosInstance.get("/members/logout");
    setSession(null);
    setProfileIdSession(null);
    dispatch({ type: "LOGOUT" });
  };

  const verifyEmail = async (values) => {
    const response = await axiosInstance.post("/members/verifyemail", values);
    return response;
  };

  const forgotPassword = async (action, values) => {
    const response = await axiosInstance.post("/members/resetpassword", {
      action,
      ...values,
    });
    return response;
  };

  // console.log("AuthProvider");

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        register,
        verifyEmail,
        getProfiles,
        switchProfile,
        forgotPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
